import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, map, filter } from 'rxjs/operators';
import {
  ViewChild,
  Component,
  Output,
  EventEmitter,
  Input,
  TemplateRef,
  OnDestroy,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatOptgroup, MatOption } from '@angular/material/core';
import { MatSelect, MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatFormFieldAppearance, SubscriptSizing } from '@angular/material/form-field';
import { removeFromArray } from '@sg/utils/src';

export interface SgAutoCompleteSelectChange<T = any> {
  source: SgAutoCompleteSelectComponent;
  value: T;
}

export interface SgAutoCompleteSelectQueryChange {
  source: SgAutoCompleteSelectComponent;
  query: string;
}

export interface SgAutoCompleteSelectOptionTemplateContext {
  $implicit: {
    matOption: MatOption;
    initialValue: any;
  };
}

export interface SgAutoCompleteSelectTriggerTemplateContext {
  $implicit: {
    matOption: MatOption | MatOption[];
    value: any | any[];
    initialValue: any | any[];
  };
}

export interface SgAutoCompleteSelectOptionGroup {
  label: string;
  value: any[];
  optGroup: boolean;
}

type SelectState = 'indeterminate' | 'none' | 'all';

export interface SgAutoCompleteSelectGroupBy {
  (option: MatOption): string;
  labels: Record<string, string>;
  selectableGroups?: boolean;
  expendableGroups?: boolean;
}

@Component({
  selector: 'sg-auto-complete-select',
  exportAs: 'sgAutoCompleteSelect',
  templateUrl: './auto-complete-select.component.html',
  styleUrls: ['./auto-complete-select.component.scss'],
  providers: [
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'sg-auto-complete-select' },
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SgAutoCompleteSelectComponent,
      multi: true,
    },
  ],
  standalone: false,
})
export class SgAutoCompleteSelectComponent<T = any> implements AfterViewInit, OnDestroy, ControlValueAccessor {
  private _panelClass: string;
  /** A unique identifier of the options */
  @Input() trackBy = (index: number, item: any) => {
    return item;
  };

  @Input() isSlimFormField: boolean;
  @Input() overrideArrowIcon: boolean = false;
  @Input() size: 'default' | 'small' = 'default';
  @Input() matchTriggerWidth: boolean = false;

  isPanelOpen = false;

  @Input()
  set panelClass(className: string) {
    this._panelClass = className;
    this.updateOverlayPanelClass();
  }
  get panelClass(): string {
    return this._panelClass;
  }
  @Input() placeholder: string;
  @Input() searchInputPlaceholder: string;
  @Input() appearance: MatFormFieldAppearance;
  @Input() label: string;
  @Input() get options(): any[] {
    return this._options;
  }
  set options(value: any[]) {
    this._options = Array.isArray(value) ? value : [];
    this.setOptions();
  }

  @Input() get groupBy(): SgAutoCompleteSelectGroupBy | undefined {
    return this._groupBy;
  }
  set groupBy(value: SgAutoCompleteSelectGroupBy | undefined) {
    if (value !== this._groupBy) {
      this._groupBy = value;
      this.setOptions();
    }
  }

  @Input() hideInput: boolean;
  @Input() subscriptSizing: SubscriptSizing = 'fixed';

  /**
   * Function to transform an option value before setting it as the value of `mat-option`.
   * Using `valueTransform` will transform all of the values in the `options` provided similar to `options.map(valueTransform)`.
   */
  @Input() valueTransform: (value: any) => T;

  /** Value of the select control. */
  @Input() get value(): T | T[] {
    return this._value;
  }
  set value(value: T | T[]) {
    this.changeValue(value);
  }

  @Input() get disabled(): boolean {
    return this._isDisabled ?? this.matSelect?.disabled ?? false;
  }
  set disabled(value: boolean) {
    this._isDisabled = coerceBooleanProperty(value);
    if (this.matSelect) {
      this.matSelect.disabled = this._isDisabled;
    }
  }

  /**
   * When true, will revert all `mat-option` styles
   */
  @Input() get clearOptionStyle(): boolean {
    return this._clearOptionStyle;
  }
  set clearOptionStyle(value: BooleanInput) {
    this._clearOptionStyle = coerceBooleanProperty(value);
  }

  @Input() get multiple(): boolean {
    return this._multiple;
  }
  set multiple(value: boolean) {
    this._multiple = coerceBooleanProperty(value);
  }

  @Input() get fitContent(): boolean {
    return this._fitContent;
  }
  set fitContent(value: BooleanInput) {
    this._fitContent = coerceBooleanProperty(value);
  }

  @Output() openedChange = new EventEmitter<boolean>();
  @Output() selectionChange = new EventEmitter<SgAutoCompleteSelectChange<T>>();
  @Output() searchQueryChange = new EventEmitter<SgAutoCompleteSelectQueryChange>();
  @Output() scrollEnd = new EventEmitter<void>();

  @ViewChild(MatSelect, { static: true }) matSelect: MatSelect;

  /**
   * The initial value of the selected control.
   * Applicable when `valueTransform` is set and holder the value/s before running `valueTransform`.
   * If `valueTransform` is not set this is identical to `value`
   */
  initialValue: any | any[];

  selectTriggerTpl?: TemplateRef<SgAutoCompleteSelectTriggerTemplateContext>;
  optionTpl: TemplateRef<SgAutoCompleteSelectOptionTemplateContext>;
  asyncTemplate: TemplateRef<any>;
  autoCompleteFormControl = new FormControl('');

  _options: any[];
  _structOptions: any[] = [];
  _value: T | T[];
  collapsedGroupNames$ = new BehaviorSubject<Map<string, boolean>>(new Map());
  groupSelectStateByGroupName$ = new BehaviorSubject<Map<string, SelectState>>(new Map());
  private _selectedOptionsStore: T[] = [];
  private _groupBy: SgAutoCompleteSelectGroupBy | undefined;
  private _multiple = false;
  private _fitContent = false;
  private _clearOptionStyle = false;
  private _onChange: any = () => {};
  private _onTouched: any = () => {};
  private _destroy = new Subject<void>();
  private _isDisabled: boolean;

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    this.autoCompleteFormControl.valueChanges
      .pipe(
        map((query) => ({ source: this, query })),
        takeUntil(this._destroy)
      )
      .subscribe(this.searchQueryChange);
  }

  ngAfterViewInit(): void {
    this.listenUserSelectionChanges();
    this.listenPanelOpenChanges();
    // Ensure disabled state is properly set after view init
    if (this._isDisabled !== undefined) {
      this.matSelect.disabled = this._isDisabled;
    }
  }

  private listenPanelOpenChanges(): void {
    this.matSelect.openedChange.pipe(takeUntil(this._destroy)).subscribe((isOpen) => {
      this.isPanelOpen = isOpen;
    });
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
  }

  writeValue(value: T | T[]): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this._isDisabled = isDisabled;
    if (this.matSelect) {
      this.matSelect.disabled = isDisabled;
    }
    // Use detectChanges to immediately update the view
    this.changeDetectorRef.detectChanges();
  }

  open() {
    this.matSelect.open();
  }

  expandCollapseGroup(group: SgAutoCompleteSelectOptionGroup) {
    const currentCollapsedGroupNames = this.collapsedGroupNames$.value;
    const isGroupCollapsed = currentCollapsedGroupNames.get(group.label);
    currentCollapsedGroupNames.set(group.label, !isGroupCollapsed);
    this.collapsedGroupNames$.next(currentCollapsedGroupNames);
  }

  toggleGroupSelection(event: any, group: SgAutoCompleteSelectOptionGroup) {
    let selectedOptions = this.value ? new Set<any>(this.value as T[]) : new Set<any>();
    event.checked
      ? this.selectAllGroupOptions(group, selectedOptions)
      : this.unselectAllGroupOptions(group, selectedOptions);
    this._selectedOptionsStore = Array.from(selectedOptions);
    this.changeValue(Array.from(selectedOptions));
    this.selectionChange.emit({ source: this, value: Array.from(selectedOptions) as any });
  }

  private setOptions() {
    const groupBy = this._groupBy;
    let currGroup = (this._structOptions = []);
    if (typeof groupBy === 'function') {
      const groups: Record<string, any> = {};
      for (const opt of this._options) {
        const groupKey = groupBy(opt);
        if (!groupKey) {
          this._structOptions.push(opt);
        } else {
          if (!(groupKey in groups)) {
            const newGroup = { label: groupBy.labels[groupKey] || groupKey, value: [], optGroup: true };
            this._structOptions.push((groups[groupKey] = newGroup));
          }
          groups[groupKey].value.push(opt);
        }
      }
    } else {
      for (const opt of this._options) {
        if (opt.optGroup) {
          currGroup = this._structOptions;
          const children = [];
          currGroup.push({
            ...opt,
            value: children,
          });
          currGroup = children;
        } else {
          currGroup.push(opt);
        }
      }
    }
  }

  private changeValue(value: T | T[]): void {
    const newValue = Array.isArray(value) ? [...value] : value;
    this._value = newValue;
    if (this.multiple) {
      this._selectedOptionsStore = (value as T[]) ?? [];
      this.determineGroupSelectState(this._selectedOptionsStore);
    }
    this._onChange(newValue);
    this._onTouched();
    this.changeDetectorRef.detectChanges();
  }

  private selectAllGroupOptions(group: SgAutoCompleteSelectOptionGroup, currentlySelectedOptions: Set<any>) {
    currentlySelectedOptions.add(group.label);
    group.value.forEach((option) => {
      const transformedGroupOption = this.valueTransform ? this.valueTransform(option) : option;
      currentlySelectedOptions.add(transformedGroupOption);
    });
    this.setGroupSelectState(group.label, 'all');
  }

  private unselectAllGroupOptions(group: SgAutoCompleteSelectOptionGroup, currentlySelectedOptions: Set<any>) {
    group.value.forEach((option) => {
      const optionValue = this.valueTransform ? this.valueTransform(option) : option;
      currentlySelectedOptions.delete(optionValue);
    });
    currentlySelectedOptions.delete(group.label);
    this.setGroupSelectState(group.label, 'none');
  }

  private listenUserSelectionChanges(): void {
    this.matSelect.optionSelectionChanges
      .pipe(
        filter((optionSelectionChange) => optionSelectionChange.isUserInput),
        takeUntil(this._destroy)
      )
      .subscribe((optionSelectionChange) => {
        const { value: optionValue, selected, multiple, group: selectedOptionGroup } = optionSelectionChange.source;
        let value = optionValue;
        if (multiple) {
          if (selected) {
            this._selectedOptionsStore.push(optionValue);
          } else {
            removeFromArray(this._selectedOptionsStore, optionValue);
          }
          value = this._selectedOptionsStore;
          if (selectedOptionGroup) {
            this.setGroupCheckboxState(selectedOptionGroup);
          }
        }
        this.changeValue(value);
        this.initialValue = this.getInitialValue(value);
        this.selectionChange.emit({ source: this, value });
      });
  }

  private getInitialValue(value: any): any | any[] {
    let initialValue = value;
    if (this.valueTransform) {
      if (this.multiple) {
        initialValue = value.map((v) => this.options.find((o) => this.valueTransform(o) === v));
      } else {
        initialValue = this.options.find((o) => this.valueTransform(o) === value);
      }
    }
    return initialValue;
  }

  private setGroupCheckboxState(selectedOptionGroup: MatOptgroup): void {
    let groupKey = selectedOptionGroup.label;
    const groupByLabels = this.groupBy?.labels;
    if (groupByLabels && Object.keys(groupByLabels).length > 0) {
      groupKey = Object.keys(groupByLabels).find((key) => groupByLabels[key] === selectedOptionGroup.label);
    }
    const groupValue = this._structOptions.find((option) => option.label === groupKey);
    if (groupValue) {
      const groupOptions = groupValue.value.map((groupOption) => groupOption.value);
      const unSelectedOptions = groupOptions.filter((option) => !this._selectedOptionsStore.includes(option));
      if (unSelectedOptions.length === 0) {
        this.setGroupSelectState(groupKey, 'all');
      } else if (unSelectedOptions.length === groupOptions.length) {
        this.setGroupSelectState(groupKey, 'none');
      } else {
        this.setGroupSelectState(groupKey, 'indeterminate');
      }
    }
  }

  private determineGroupSelectState(selectedOptions: T[]) {
    if (this.groupBy) {
      const selectedGroups = this._structOptions.filter((group) => selectedOptions.includes(group.label));
      if (selectedGroups.length > 0) {
        const selectedGroupNames = selectedGroups.map((group) => group.label);
        const selectedOptionsWithoutGroup = selectedOptions.filter((option) => !selectedGroupNames.includes(option));
        selectedGroups.forEach((group) => {
          let groupOptions = group.value;
          if (this.valueTransform) {
            groupOptions = group.value.map((option) => this.valueTransform(option));
          }
          const hasUnselectedOptions =
            groupOptions.filter((groupOption) => !selectedOptionsWithoutGroup.includes(groupOption)).length > 0;
          this.setGroupSelectState(group.label, hasUnselectedOptions ? 'indeterminate' : 'all');
        });
      }
    }
  }

  private setGroupSelectState(key: string, value: SelectState): void {
    const groupSelectStateMap = this.groupSelectStateByGroupName$.value;
    groupSelectStateMap.set(key, value);
    this.groupSelectStateByGroupName$.next(groupSelectStateMap);
  }

  private updateOverlayPanelClass(): void {
    if (this.matSelect && this._panelClass) {
      this.matSelect._overlayPanelClass = ['sg-auto-complete-select', ...this._panelClass.split(' ')];
    }
  }
}
